import { useState } from "react";
import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import Checkbox from "../../Form/Checkbox";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "../../Buttons/Button";
import TableContainer from "../../Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import { ConfirmAlert } from "../../Alerts/Alerts";
import { useTranslationApp } from "../../../lib/i18next";
import {
  ALERT_DELETE_WEBPAGE_TITLE,
  ALERT_DELETE_WEBPAGE_DESCRIPTION,
  ALERT_DELETE_WEBPAGES_TITLE,
  ALERT_DELETE_WEBPAGES_DESCRIPTION,
  BUTTON_DELETE,
  COLUMN_NAME_ACTIONS,
  COLUMN_NAME_TYPE,
  COLUMN_NAME_URL,
  GOOGLE_WEBPAGES_NOT_ADDED,
  GOOGLE_WEBPAGE_CRITERION_URL_EQUALS,
  SELECTED,
} from "../../../i18n/keysTranslations";

const WebpagesTable = ({
  webpages = [],
  onEdit,
  onDelete,
  disabled = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [selected, setSelected] = useState([]);
  const { t } = useTranslationApp();

  const onChangePage = (newPage) => setPage(newPage);

  const onSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(webpages.map((wp) => wp.id));
      return;
    }
    setSelected([]);
  };

  const onSelectOne = (id) => {
    const idx = selected.indexOf(id);
    if (idx === -1) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter((s) => s !== id));
    }
  };

  const onDeleteSelected = async () => {
    const result = await ConfirmAlert({
      title: t(
        selected.length > 1
          ? ALERT_DELETE_WEBPAGES_TITLE
          : ALERT_DELETE_WEBPAGE_TITLE
      ),
      text: t(
        selected.length > 1
          ? ALERT_DELETE_WEBPAGES_DESCRIPTION
          : ALERT_DELETE_WEBPAGE_DESCRIPTION,
        { value: selected.length }
      ),
    });
    if (!result.isConfirmed) return;
    onDelete(selected);
    setSelected([]);
  };

  const onDeleteOne = async (id) => {
    const result = await ConfirmAlert({
      title: t(ALERT_DELETE_WEBPAGE_TITLE),
      text: t(ALERT_DELETE_WEBPAGE_DESCRIPTION),
    });
    if (!result.isConfirmed) return;
    onDelete([id]);
    setSelected((prev) => prev.filter((s) => s !== id));
  };

  const visibleRows =
    rowsPerPage > 0
      ? webpages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : webpages;

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <TableContainer
        page={page}
        hasMarginBottom={false}
        emptyText={GOOGLE_WEBPAGES_NOT_ADDED}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        keys={webpages}
        sx={{
          maxHeight: "calc(100vh - 400px)",
          borderRadius: "0px !important",
        }}
        customComponentBottomLeft={
          selected.length > 0 ? (
            <Typography variant="body1" sx={{ ml: 1 }}>
              {`${selected.length} ${t(SELECTED)}`}
            </Typography>
          ) : null
        }
      >
        <TableHead>
          <TableRow>
            {!disabled && (
              <TableCell isHeader={true} resizable={false} sx={{ width: 32, minWidth: "32px" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < webpages.length
                    }
                    checked={
                      webpages.length > 0 && selected.length === webpages.length
                    }
                    onChange={onSelectAll}
                    sx={{
                      "&.MuiCheckbox-indeterminate": {
                        color: "white !important",
                      },
                    }}
                  />
                  {selected.length > 0 && (
                    <Button color="secondary" onClick={onDeleteSelected}>
                      {t(BUTTON_DELETE)}
                    </Button>
                  )}
                </Box>
              </TableCell>
            )}
            <TableCell isHeader={true} sx={{ minWidth: "100px" }}>
              {t(COLUMN_NAME_TYPE)}
            </TableCell>
            <TableCell isHeader={true}>{t(COLUMN_NAME_URL)}</TableCell>
            {!disabled && (
              <TableCell isHeader={true} resizable={false} sx={{ minWidth: "80px" }}>
                {t(COLUMN_NAME_ACTIONS)}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((wp, index) => {
            const isLastRow = index === webpages.length - 1;
            const isSelected = selected.indexOf(wp.id) !== -1;
            return (
              <TableRow key={wp.id} selected={isSelected}>
                {!disabled && (
                  <TableCell
                    indexRow={index}
                    sx={{ width: 32, minWidth: "32px" }}
                    isLastRow={isLastRow}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onSelectOne(wp.id)}
                    />
                  </TableCell>
                )}
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ minWidth: "100px" }}
                >
                  {t(GOOGLE_WEBPAGE_CRITERION_URL_EQUALS)}
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ whiteSpace: "pre-wrap", maxWidth: "400px" }}
                >
                  {wp.value}
                </TableCell>
                {!disabled && (
                  <TableCell indexRow={index} isLastRow={isLastRow}>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        sx={{ pl: 0 }}
                        onClick={() => onEdit(wp)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => onDeleteOne(wp.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

export default WebpagesTable;
